import { Input, InputProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

/*
 * v2 shipped `isDisabled`/`isReadOnly`; v3's real `Input` only understands
 * the native `disabled`/`readOnly` HTML attributes. Was a raw
 * `export { Input as MInput } from '@chakra-ui/react'` passthrough in
 * `chakra.tsx` until now — every `isDisabled`/`isReadOnly` passed to it was
 * a silent no-op (input stayed editable regardless). Real `disabled`/
 * `readOnly` still work directly and take precedence if a caller somehow
 * passes both.
 *
 * v2 also shipped `isInvalid`; v3's `Input` has no `invalid` prop. It sets
 * `aria-invalid`, which is what v3's `_invalid` condition matches
 * (`[aria-invalid=true]`) and what v2 rendered too. Wrapping the input in its
 * own `Field.Root` instead would give it that inner field's id, unlinking it
 * from an enclosing `MFormField`'s label and error text.
 */
export interface MInputProps extends InputProps {
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isInvalid?: boolean;
}

const MInput = forwardRef<HTMLInputElement, MInputProps>(
  ({ isDisabled, isReadOnly, isInvalid, disabled, readOnly, ...rest }, ref) => (
    <Input
      ref={ref}
      disabled={disabled ?? isDisabled}
      readOnly={readOnly ?? isReadOnly}
      // `undefined` when not invalid, so an enclosing field's state still applies.
      aria-invalid={isInvalid || undefined}
      {...rest}
    />
  ),
);

MInput.displayName = 'MInput';

export default MInput;
